import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext.tsx';
import { useRealtime } from './RealtimeContext.tsx';
import {
  DEFAULT_PERMISSION_MAP,
  PermissionKey,
  PermissionMap,
  roleHasPermission,
  setPermissionMap,
} from '../../shared/permissions.ts';

// What each role may do (set by an administrator on the Roles & Access screen). Loaded after sign-in
// and refreshed live when an administrator changes it. The shared helpers read the same map, so
// rules such as "who may complete a task" follow it everywhere without extra wiring.

interface PermissionsContextType {
  permissions: PermissionMap;
  /** Does the signed-in person's role hold this permission? */
  can: (key: PermissionKey) => boolean;
  reload: () => Promise<void>;
  applyMap: (map: PermissionMap) => void;
}

const PermissionsContext = createContext<PermissionsContextType>({
  permissions: DEFAULT_PERMISSION_MAP,
  can: (key) => false,
  reload: async () => {},
  applyMap: () => {},
});

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const { subscribe } = useRealtime();
  const [permissions, setPermissions] = useState<PermissionMap>(DEFAULT_PERMISSION_MAP);

  const applyMap = useCallback((map: PermissionMap) => {
    setPermissionMap(map);
    setPermissions(map);
  }, []);

  const reload = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/permissions', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.permissions) applyMap(data.permissions);
      }
    } catch {
      // keep what we have (the shipped defaults until the first load works)
    }
  }, [token, user?.id, applyMap]);

  useEffect(() => {
    if (user && token) reload();
  }, [user?.id, token, reload]);

  useEffect(() => {
    const unsubscribe = subscribe((event: any) => {
      if (event.type === 'permissions_updated' && event.data?.permissions) {
        applyMap(event.data.permissions);
      }
    });
    return unsubscribe;
  }, [subscribe, applyMap]);

  const can = useCallback(
    (key: PermissionKey) => roleHasPermission(user?.role, key),
    // permissions is a dependency on purpose: a new map must produce a new `can`
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.role, permissions]
  );

  return (
    <PermissionsContext.Provider value={{ permissions, can, reload, applyMap }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionsContext);
